import { useState } from "react";
import { ChevronDown, ChevronRight, Calculator, Fuel, Activity } from "lucide-react";

const MEFuelVsRpm = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const calculations = [
    {
      id: "fuel-consumption-calculation",
      title: "Fuel Consumption Measurement",
      description: "Measures the amount of fuel consumed during operation",
      formula: "Fuel Consumption = Total fuel used ÷ Operating time",
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
    },
    {
      id: "rpm-measurement",
      title: "Propeller RPM Measurement",
      description: "Measures rotational speed of the main propulsion propeller",
      formula: "RPM = Number of complete revolutions ÷ Time in minutes",
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
    },
    {
      id: "fuel-efficiency-ratio",
      title: "Fuel Efficiency Ratio",
      description: "Analyzes fuel consumption relative to propeller speed",
      formula: "Fuel per RPM = Fuel Consumption ÷ RPM",
      borderColor: "border-purple-200",
      bgColor: "bg-purple-50",
      textColor: "text-purple-700",
    },
    {
      id: "daily-average-calculation",
      title: "Daily Average Values",
      description: "Calculates mean values across all measurements for each day",
      formula: "Daily Average = Sum of all measurements ÷ Number of measurements",
      borderColor: "border-amber-200",
      bgColor: "bg-amber-50",
      textColor: "text-amber-700",
    },
    {
      id: "operational-efficiency",
      title: "Operational Efficiency Analysis",
      description: "Evaluates fuel consumption patterns across different RPM ranges",
      formula: "Efficiency = Optimal Fuel Consumption ÷ Actual Fuel Consumption × 100%",
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
    }
  ];

  const dataFields = [
    {
      name: "Fuel Consumption",
      unit: "consumption units",
      description: "Amount of fuel consumed during measurement period"
    },
    {
      name: "Propeller RPM",
      unit: "revolutions per minute",
      description: "Rotational speed of the main propulsion propeller"
    }
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#fce3d2] rounded-lg">
              <Activity className="w-6 h-6 text-[#d35400]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Fuel vs RPM Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Fuel efficiency analysis and operational optimization
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            {/* Data Fields */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Fuel className="w-4 h-4" />
                Operational Measurement Fields
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {dataFields.map((field, index) => (
                  <div key={index} className="p-4 border border-gray-200 rounded-lg bg-white">
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="font-medium text-gray-800">{field.name}</h5>
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                        {field.unit}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{field.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <Calculator className="w-4 h-4" />
                Efficiency Calculations & Formulas
              </h4>
              <div className="grid grid-cols-2 gap-4">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-4 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <h4 className="font-semibold text-gray-800 mb-2">
                      {calc.title}
                    </h4>

                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm font-medium text-gray-700">Description:</p>
                          <p className="text-sm text-gray-600">{calc.description}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">Formula:</p>
                          <p className="text-sm font-semibold bg-white p-2 rounded border">
                            {calc.formula}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MEFuelVsRpm;